import {
  type LabelLayout,
  type LabelMedium,
  type LabelPrintOptions,
  type LayoutWarning,
  labelPrintOptions,
  labelUrl,
  layoutLabels,
  MEDIA_PRESETS,
} from "@bt/domain";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  FileDownIcon,
  ImageDownIcon,
  MoreHorizontalIcon,
  PencilIcon,
  PrinterIcon,
  Trash2Icon,
  TriangleAlertIcon,
} from "lucide-react";
import { useEffect, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Field } from "@/components/app/field";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Segmented } from "@/components/ui/misc";
import { Select } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toLanguage } from "@/i18n";
import { ApiError, errorMessage } from "@/lib/errors";
import { apiPath } from "@/lib/garage";
import { useSystemInfo } from "@/lib/system";
import { useTRPC } from "@/lib/trpc";
import { MediumDialog } from "./medium-dialog";
import { LabelPreview } from "./preview";
import { type LabelItem, useShowError } from "./shared";

const NEW_MEDIUM = "new";
/** Where the preview's QR codes point before the installation has an address (development). */
const SAMPLE_URL = "https://app.example.com";

/** Downloads one print of the basket: the server renders it and marks the labels printed. */
async function download(body: unknown): Promise<void> {
  const response = await fetch(apiPath("/labels/print"), {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw await ApiError.from(response);
  const name =
    response.headers.get("content-disposition")?.match(/filename="([^"]+)"/)?.[1] ?? "labels";
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** The print page (ADR 0018 › Printing): medium, options, preview, download. */
export function PrintPanel({ basket }: { basket: LabelItem[] }) {
  const { t, i18n } = useTranslation();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const showError = useShowError();
  const publicUrl = useSystemInfo()?.capabilities.publicUrl ?? null;
  const { data: setup } = useQuery(trpc.labels.printSetup.queryOptions());
  const { data: media = [] } = useQuery(trpc.labels.media.list.queryOptions());
  const [mediaRef, setMediaRef] = useState<string | null>(null);
  const [options, setOptions] = useState<LabelPrintOptions | null>(null);
  const [skip, setSkip] = useState("0");
  const [creating, setCreating] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [askEmpty, setAskEmpty] = useState(false);
  const [printing, setPrinting] = useState<"pdf" | "png" | null>(null);
  const empty = useMutation({
    ...trpc.labels.emptyBasket.mutationOptions(),
    onSuccess: () => toast.success(t("labels.basketEmptied")),
    onError: showError,
  });
  const remove = useMutation({ ...trpc.labels.media.delete.mutationOptions(), onError: showError });

  // The garage's last setup, once it has loaded; never overwritten while someone edits.
  useEffect(() => {
    if (!setup) return;
    setMediaRef((current) => current ?? setup.mediaRef);
    setOptions((current) => current ?? setup.options);
  }, [setup]);

  const custom = media.find((m) => `custom:${m.id}` === mediaRef);
  const medium: LabelMedium | null =
    MEDIA_PRESETS.find((p) => `preset:${p.id}` === mediaRef)?.medium ?? custom?.medium ?? null;
  const skipCount = Math.max(0, Math.min(1000, Number(skip) || 0));
  const layout = useMemo(
    () =>
      medium && options
        ? layoutLabels({
            medium,
            options,
            labels: basket.map((l) => ({
              code: l.code,
              partNumber: l.part?.primaryNumber ?? null,
            })),
            skip: medium.kind === "sheet" ? skipCount : 0,
          })
        : null,
    [medium, options, basket, skipCount],
  );
  const urls = useMemo(
    () => basket.map((l) => labelUrl(publicUrl ?? SAMPLE_URL, l.code)),
    [basket, publicUrl],
  );
  if (!options || !mediaRef || !medium || !layout) return null;
  const set = (change: Partial<LabelPrintOptions>) =>
    setOptions(labelPrintOptions.parse({ ...options, ...change }));

  const print = async (format: "pdf" | "png") => {
    setPrinting(format);
    try {
      const body = {
        media: mediaRef,
        options,
        format,
        skip: medium.kind === "sheet" ? skipCount : 0,
      };
      await download(body);
      if (options.pickList) {
        await download({ ...body, output: "pickList", language: toLanguage(i18n.language) });
      }
      await queryClient.invalidateQueries();
      setAskEmpty(true);
    } catch (error) {
      toast.error(errorMessage(error, t));
    } finally {
      setPrinting(null);
    }
  };

  const mediumOptions = [
    ...MEDIA_PRESETS.map((p) => ({ value: `preset:${p.id}`, label: p.name })),
    ...media.map((m) => ({ value: `custom:${m.id}`, label: m.name })),
    { value: NEW_MEDIUM, label: t("labels.customMediumNew") },
  ];
  const page = layout.pages[0];
  return (
    <Card className="flex min-w-0 flex-col gap-5 p-4 sm:p-5">
      <h2 className="flex items-center gap-2 font-semibold tracking-tight">
        <PrinterIcon className="size-4" />
        {t("labels.print")}
      </h2>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={t("labels.medium")}>
          <div className="flex gap-1">
            <Select
              aria-label={t("labels.medium")}
              className="min-w-0 flex-1"
              value={mediaRef}
              onValueChange={(value) =>
                value === NEW_MEDIUM ? setCreating(true) : setMediaRef(value)
              }
              options={mediumOptions}
            />
            {custom ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" aria-label={t("labels.mediumActions")}>
                    <MoreHorizontalIcon />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onSelect={() => setRenaming(true)}>
                    <PencilIcon />
                    {t("labels.renameMedium")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => setDeleting(true)}>
                    <Trash2Icon />
                    {t("labels.deleteMedium")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </div>
        </Field>
        <Field label={t("labels.qrSize")}>
          <Segmented<"3" | "4">
            aria-label={t("labels.qrSize")}
            value={String(options.dotsPerModule) as "3" | "4"}
            onChange={(value) =>
              set({ dotsPerModule: Number(value) as 3 | 4, fontSize: null, cellWidth: null })
            }
            options={[
              { value: "3", label: t("labels.qrSmall") },
              { value: "4", label: t("labels.qrLarge") },
            ]}
          />
        </Field>
        <Field label={t("labels.gap")} htmlFor="label-gap" hint={t("labels.gapHint")}>
          <Input
            id="label-gap"
            inputMode="decimal"
            defaultValue={String(options.gap)}
            onBlur={(event) => {
              const gap = Number(event.target.value.replace(",", "."));
              if (gap >= 1 && gap <= 20) set({ gap });
              else event.target.value = String(options.gap);
            }}
          />
        </Field>
        {medium.kind === "sheet" ? (
          <Field label={t("labels.skip")} htmlFor="label-skip" hint={t("labels.skipHint")}>
            <Input
              id="label-skip"
              inputMode="numeric"
              value={skip}
              onChange={(event) => setSkip(event.target.value.replace(/\D/g, ""))}
            />
          </Field>
        ) : null}
      </div>
      <div className="flex flex-col divide-y divide-border rounded-lg border border-border">
        <Toggle
          label={t("labels.showCode")}
          checked={options.showCode}
          onChange={(showCode) => set({ showCode })}
        />
        <Toggle
          label={t("labels.showPartNumber")}
          hint={t("labels.showPartNumberHint")}
          checked={options.showPartNumber}
          onChange={(showPartNumber) => set({ showPartNumber, cellWidth: null })}
        />
        <Toggle
          label={t("labels.pickList")}
          hint={t("labels.pickListHint")}
          checked={options.pickList}
          onChange={(pickList) => set({ pickList })}
        />
        {medium.kind === "roll" ? (
          <Toggle
            label={t("labels.printableOnly")}
            hint={t("labels.printableOnlyHint")}
            checked={options.printableWidthOnly}
            onChange={(printableWidthOnly) => set({ printableWidthOnly })}
          />
        ) : null}
      </div>

      <Figures layout={layout} kind={medium.kind} count={basket.length} />
      <Warnings warnings={layout.warnings} onFix={(change) => set(change)} />
      {page ? (
        // Scrolls on narrow screens, so it can take the focus (and the arrow keys).
        <section
          aria-label={t("labels.previewTitle")}
          // biome-ignore lint/a11y/noNoninteractiveTabindex: a scrolling region must be focusable
          tabIndex={0}
          className="max-h-[60vh] overflow-auto rounded-lg bg-muted p-4 outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
        >
          <LabelPreview layout={layout} urls={urls} title={t("labels.previewTitle")} />
        </section>
      ) : null}

      <div className="flex flex-col gap-2">
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            disabled={!page || printing !== null || !publicUrl}
            onClick={() => void print("pdf")}
          >
            <FileDownIcon />
            {t("labels.downloadPdf")}
          </Button>
          <Button
            variant="outline"
            disabled={!page || printing !== null || !publicUrl}
            onClick={() => void print("png")}
          >
            <ImageDownIcon />
            {t("labels.downloadPng")}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">{t("labels.printHint")}</p>
      </div>

      <MediumDialog open={creating} onOpenChange={setCreating} onSaved={setMediaRef} />
      {custom ? (
        <RenameMediumDialog
          key={custom.id}
          open={renaming}
          onOpenChange={setRenaming}
          id={custom.id}
          name={custom.name}
        />
      ) : null}
      <ConfirmDialog
        open={deleting}
        onOpenChange={setDeleting}
        title={t("labels.deleteMediumTitle", { name: custom?.name ?? "" })}
        confirmLabel={t("common.delete")}
        destructive
        pending={remove.isPending}
        onConfirm={() =>
          custom &&
          remove.mutate(
            { id: custom.id },
            {
              onSuccess: () => {
                setDeleting(false);
                setMediaRef(`preset:${MEDIA_PRESETS[0].id}`);
              },
            },
          )
        }
      />
      <ConfirmDialog
        open={askEmpty}
        onOpenChange={setAskEmpty}
        title={t("labels.printedTitle")}
        description={t("labels.printedBody")}
        confirmLabel={t("labels.emptyBasket")}
        cancelLabel={t("labels.keepBasket")}
        pending={empty.isPending}
        onConfirm={() => empty.mutate(undefined, { onSuccess: () => setAskEmpty(false) })}
      />
    </Card>
  );
}

function Toggle({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  const id = useId();
  return (
    <div className="flex items-center justify-between gap-4 px-3 py-2.5">
      <label htmlFor={id} className="min-w-0">
        <span className="block text-sm font-medium">{label}</span>
        {hint ? <span className="block text-xs text-muted-foreground">{hint}</span> : null}
      </label>
      <Switch id={id} checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

/** What the layout comes to: columns and rows, the strip's length or the pages. */
function Figures({
  layout,
  kind,
  count,
}: {
  layout: LabelLayout;
  kind: "roll" | "sheet";
  count: number;
}) {
  const { t, i18n } = useTranslation();
  const mm = (value: number) => Math.round(value).toLocaleString(i18n.language);
  const figures =
    kind === "roll"
      ? [
          t("labels.figureColumns", { count: layout.columns }),
          t("labels.figureRows", { count: layout.rows }),
          t("labels.figureStrip", { length: mm(layout.pages[0]?.height ?? 0) }),
        ]
      : [
          t("labels.figurePerPage", { count: layout.columns * layout.rows }),
          t("labels.figurePages", { count: layout.pages.length }),
        ];
  return (
    <p className="text-sm text-muted-foreground">
      {[t("labels.figureLabels", { count }), ...figures].join(" · ")}
    </p>
  );
}

/** What doesn't fit, with the fix where there is one. */
function Warnings({
  warnings,
  onFix,
}: {
  warnings: LayoutWarning[];
  onFix: (change: Partial<LabelPrintOptions>) => void;
}) {
  const { t } = useTranslation();
  if (warnings.length === 0) return null;
  return (
    <div className="flex flex-col gap-2">
      {warnings.map((warning) => (
        <div
          key={warning.kind}
          role="alert"
          className="flex flex-col gap-2 rounded-lg border border-partial/40 bg-partial-bg px-3 py-2 text-sm text-partial-fg"
        >
          <span className="flex items-start gap-2">
            <TriangleAlertIcon className="mt-0.5 size-4 shrink-0" />
            {warning.kind === "codeTooWide"
              ? t("labels.warnCodeTooWide", {
                  width: warning.minCellWidth,
                  size: warning.maxFontSize,
                })
              : warning.kind === "partNumberCut"
                ? t("labels.warnPartNumberCut", { count: warning.codes.length })
                : warning.kind === "cellTooLarge"
                  ? t("labels.warnCellTooLarge")
                  : t("labels.warnCellLargerThanLabel")}
          </span>
          {warning.kind === "codeTooWide" ? (
            <div className="flex flex-wrap gap-2 pl-6">
              <Button
                size="sm"
                variant="outline"
                onClick={() => onFix({ cellWidth: warning.minCellWidth })}
              >
                {t("labels.fixWiderCell", { width: warning.minCellWidth })}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onFix({ fontSize: warning.maxFontSize })}
              >
                {t("labels.fixSmallerFont", { size: warning.maxFontSize })}
              </Button>
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}

/** A saved medium's new name. */
function RenameMediumDialog({
  open,
  onOpenChange,
  id,
  name,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  id: string;
  name: string;
}) {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const showError = useShowError();
  const [value, setValue] = useState(name);
  const rename = useMutation({ ...trpc.labels.media.rename.mutationOptions(), onError: showError });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (!value.trim()) return;
            rename.mutate({ id, name: value }, { onSuccess: () => onOpenChange(false) });
          }}
        >
          <DialogHeader title={t("labels.renameMedium")} />
          <DialogBody>
            <Field label={t("labels.mediumName")} htmlFor="medium-rename">
              <Input
                id="medium-rename"
                value={value}
                onChange={(event) => setValue(event.target.value)}
              />
            </Field>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={!value.trim() || rename.isPending}>
              {t("common.save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
